import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import { apiService } from '../services/api';
import './PaymentStatusPage.scss';

interface PaymentStatusData {
  success: boolean;
  cart_id: number;
  status: string;
  is_closed: boolean;
  processed: boolean;
  payment_gateway: string | null;
  total: number;
  currency: string | null;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
}

const PaymentStatusPage: React.FC = () => {
  const { cartId } = useParams<{ cartId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatusData | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Get payment gateway from URL params
  const gateway = searchParams.get('gateway');
  const status = searchParams.get('status');
  const paymentReferenceId = searchParams.get('payment_reference_id');

  useEffect(() => {
    if (cartId) {
      checkPaymentStatus();
    }
  }, [cartId]);

  const checkPaymentStatus = async () => {
    if (!cartId) return;
    
    try {
      setCheckingStatus(true);
      setError(null);
      
      const statusData = await apiService.payment.getStatus(parseInt(cartId));
      setPaymentStatus(statusData);
      
      // If this is a callback from payment gateway, handle it
      if (gateway && status) {
        await handlePaymentCallback();
      }
      
    } catch (error) {
      console.error('Error checking payment status:', error);
      setError('Failed to check payment status. Please try again.');
    } finally {
      setLoading(false);
      setCheckingStatus(false);
    }
  };

  const handlePaymentCallback = async () => {
    if (!cartId || !gateway) return;
    
    try {
      const callbackParams: Record<string, any> = {};
      if (status) callbackParams.status = status;
      if (paymentReferenceId) callbackParams.payment_reference_id = paymentReferenceId;
      
      const result = await apiService.payment.handleCallback(
        parseInt(cartId), 
        gateway, 
        callbackParams
      );
      
      if (result.success) {
        // Payment successful, redirect to confirmation
        navigate(result.redirect_url);
      } else {
        // Payment failed, show error
        setError(result.error || 'Payment failed');
      }
    } catch (error) {
      console.error('Error handling payment callback:', error);
      setError('Failed to process payment callback');
    }
  };

  const getStatusIcon = () => {
    if (!paymentStatus) return 'fas fa-question-circle';
    
    if (paymentStatus.is_closed && paymentStatus.processed) {
      return 'fas fa-check-circle text-success';
    } else if (paymentStatus.status === 'closed' && !paymentStatus.processed) {
      return 'fas fa-exclamation-triangle text-warning';
    } else {
      return 'fas fa-clock text-info';
    }
  };

  const getStatusMessage = () => {
    if (!paymentStatus) return 'Checking payment status...';
    
    if (paymentStatus.is_closed && paymentStatus.processed) {
      return 'Payment completed successfully!';
    } else if (paymentStatus.status === 'closed' && !paymentStatus.processed) {
      return 'Payment is being processed. Please wait...';
    } else if (paymentStatus.status === 'open') {
      return 'Payment is still pending.';
    } else {
      return 'Payment status unknown.';
    }
  };

  const getStatusColor = () => {
    if (!paymentStatus) return 'info';
    
    if (paymentStatus.is_closed && paymentStatus.processed) {
      return 'success';
    } else if (paymentStatus.status === 'closed' && !paymentStatus.processed) {
      return 'warning';
    } else {
      return 'info';
    }
  };

  const formatPrice = (amount: number, currency: string) => {
    const symbol = currency === 'BDT' ? '৳' : currency === 'THB' ? '฿' : '$';
    return `${symbol}${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <Container className="payment-status-page">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="glass">
              <Card.Body className="text-center py-5">
                <Spinner animation="border" variant="primary" className="mb-3" />
                <h5>Checking Payment Status...</h5>
                <p className="text-muted">Please wait while we verify your payment.</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="payment-status-page">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="glass">
            <Card.Body className="text-center py-4">
              <div className="status-icon mb-3">
                <i className={`${getStatusIcon()} fa-3x`}></i>
              </div>
              
              <h4 className="mb-3">Payment Status</h4>
              
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              
              {paymentStatus && (
                <>
                  <Alert variant={getStatusColor()} className="mb-3">
                    <strong>{getStatusMessage()}</strong>
                  </Alert>
                  
                  <div className="payment-details mb-4">
                    <Row className="text-start">
                      <Col xs={6}>
                        <strong>Cart ID:</strong>
                      </Col>
                      <Col xs={6}>
                        #{paymentStatus.cart_id}
                      </Col>
                      
                      <Col xs={6}>
                        <strong>Amount:</strong>
                      </Col>
                      <Col xs={6}>
                        {formatPrice(paymentStatus.total, paymentStatus.currency || 'USD')}
                      </Col>
                      
                      <Col xs={6}>
                        <strong>Gateway:</strong>
                      </Col>
                      <Col xs={6}>
                        {paymentStatus.payment_gateway || 'N/A'}
                      </Col>
                      
                      <Col xs={6}>
                        <strong>Status:</strong>
                      </Col>
                      <Col xs={6}>
                        <span className={`badge bg-${getStatusColor()}`}>
                          {paymentStatus.status}
                        </span>
                      </Col>
                    </Row>
                  </div>
                </>
              )}
              
              <div className="d-grid gap-2">
                <Button
                  variant="outline-primary"
                  onClick={checkPaymentStatus}
                  disabled={checkingStatus}
                >
                  {checkingStatus ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-sync-alt me-2"></i>
                      Refresh Status
                    </>
                  )}
                </Button>
                
                <Button
                  variant="primary"
                  onClick={() => navigate('/cart')}
                >
                  <i className="fas fa-shopping-cart me-2"></i>
                  Back to Cart
                </Button>
                
                {paymentStatus?.is_closed && paymentStatus?.processed && (
                  <Button
                    variant="success"
                    onClick={() => navigate(`/checkout/${paymentStatus.cart_id}/confirmation`)}
                  >
                    <i className="fas fa-receipt me-2"></i>
                    View Confirmation
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PaymentStatusPage;
